/**
 * SidecarDetailView — full-width detail page for a selected sidecar,
 * shown in the main content area when a sidecar is selected from the sidebar.
 */

import type {
    SidecarCapability,
    SidecarInfo,
    SidecarManifest,
} from "@magic-md/engine-client/protocol";
import { resolveSidecarManifest } from "@magic-md/engine-client/protocol";
import { useI18n, useT } from "@magic-md/i18n/react";
import { CollapsibleSidebar, JsonTree } from "@magic-md/plugin-sdk";
import {
    Activity,
    ArrowLeft,
    Cloud,
    Circle,
    Cpu,
    Filter,
    Layers,
    Loader2,
    PanelLeftClose,
    PanelLeftOpen,
    Play,
    PowerOff,
    RefreshCw,
    Server,
    Settings2,
    Terminal,
    Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { getPlatformService } from "../platform";
import { pluginUIRegistry } from "../plugins/plugin-ui-registry";
import { useEditorStore } from "../store/editor-store";
import { useEngineOverviewStore } from "../store/engine-overview-store";
import { useSettingsStore } from "../store/settings-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

/* ── Helpers ── */

function getPluginsForSidecar(sidecarId: string): string[] {
    const plugins: string[] = [];
    for (const [name, plugin] of pluginUIRegistry.getAll()) {
        if (plugin.manifest.backends?.some((b) => b.id === sidecarId)) {
            plugins.push(name);
        }
    }
    return plugins;
}

function isCloudSidecar(
    sidecarInfo: SidecarInfo | null,
    manifest: SidecarManifest,
): boolean {
    return (
        sidecarInfo?.sourceKind === "cloud" ||
        sidecarInfo?.transport?.type === "cloud-worker-http" ||
        manifest.sourceKind === "cloud" ||
        manifest.transport?.type === "cloud-worker-http"
    );
}

function transportLabel(
    sidecarInfo: SidecarInfo | null,
    manifest: SidecarManifest,
): string {
    return (
        sidecarInfo?.transport?.type ??
        manifest.transport?.type ??
        manifest.protocol ??
        "jsonrpc-stdio"
    );
}

type SidecarSourceKind = "local" | "cloud";

interface SidecarSourceDetailRow {
    key: string;
    labelKey: string;
    value: string;
    valueKey?: string;
    href?: string;
    mono?: boolean;
}

function sidecarSourceKindFor(
    sidecarInfo: SidecarInfo | null,
    manifest: SidecarManifest,
): SidecarSourceKind {
    return isCloudSidecar(sidecarInfo, manifest) ? "cloud" : "local";
}

function sidecarSourceLabelKey(kind: SidecarSourceKind): string {
    return kind === "cloud" ? "sidecarSource.cloud" : "sidecarSource.local";
}

function sidecarSourceTooltipKey(kind: SidecarSourceKind): string {
    return kind === "cloud"
        ? "sidecarSource.cloud.tooltip"
        : "sidecarSource.local.tooltip";
}

function mergeTransportInfo(
    base: SidecarInfo["transport"] | undefined,
    override: SidecarManifest["transport"] | undefined,
): SidecarInfo["transport"] | undefined {
    if (!base) return override;
    if (!override) return base;
    return {
        type: override.type ?? base.type,
        endpoint: override.endpoint ?? base.endpoint,
        manifestUrl: override.manifestUrl ?? base.manifestUrl,
        healthUrl: override.healthUrl ?? base.healthUrl,
        sourceId: override.sourceId ?? base.sourceId,
        sourceUrl: override.sourceUrl ?? base.sourceUrl,
    };
}

function sidecarTransportInfo(
    sidecarInfo: SidecarInfo | null,
    manifest: SidecarManifest,
): SidecarInfo["transport"] | undefined {
    return mergeTransportInfo(manifest.transport, sidecarInfo?.transport);
}

function getSidecarPrimaryUrl(
    sidecarInfo: SidecarInfo | null,
    manifest: SidecarManifest,
): string | undefined {
    const transport = sidecarTransportInfo(sidecarInfo, manifest);
    return (
        transport?.manifestUrl ??
        transport?.sourceUrl ??
        transport?.endpoint ??
        transport?.healthUrl
    );
}

function getUrlHost(value: string | undefined): string | undefined {
    if (!value) return undefined;
    try {
        return new URL(value).host;
    } catch {
        return undefined;
    }
}

function inferSidecarProvider(
    sidecarInfo: SidecarInfo | null,
    manifest: SidecarManifest,
): string | undefined {
    const host = getUrlHost(getSidecarPrimaryUrl(sidecarInfo, manifest));
    if (host?.endsWith(".workers.dev")) return "Cloudflare Workers";
    if (host?.endsWith(".pages.dev")) return "Cloudflare Pages";
    return sidecarSourceKindFor(sidecarInfo, manifest) === "cloud"
        ? "Cloudflare"
        : undefined;
}

function isHttpUrl(value: string): boolean {
    return /^https?:\/\//i.test(value);
}

function pushSourceRow(
    rows: SidecarSourceDetailRow[],
    key: string,
    labelKey: string,
    value: string | undefined,
    options: { mono?: boolean; url?: boolean } = {},
): void {
    if (!value) return;
    rows.push({
        key,
        labelKey,
        value,
        href: options.url && isHttpUrl(value) ? value : undefined,
        mono: options.mono,
    });
}

function dedupeRows(rows: SidecarSourceDetailRow[]): SidecarSourceDetailRow[] {
    const seen = new Set<string>();
    return rows.filter((row) => {
        const key = `${row.key}:${row.value}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function getSidecarSourceDetailRows(
    sidecarInfo: SidecarInfo | null,
    manifest: SidecarManifest,
): SidecarSourceDetailRow[] {
    const kind = sidecarSourceKindFor(sidecarInfo, manifest);
    const transport = sidecarTransportInfo(sidecarInfo, manifest);
    const rows: SidecarSourceDetailRow[] = [
        {
            key: "type",
            labelKey: "sidecarSource.detail.type",
            value: kind,
            valueKey: sidecarSourceLabelKey(kind),
        },
    ];

    pushSourceRow(
        rows,
        "version",
        "sidecarSource.detail.deployedVersion",
        manifest.version ? `v${manifest.version}` : undefined,
    );
    pushSourceRow(
        rows,
        "provider",
        "sidecarSource.detail.provider",
        inferSidecarProvider(sidecarInfo, manifest),
    );
    pushSourceRow(
        rows,
        "transport",
        "sidecarSource.detail.transport",
        transportLabel(sidecarInfo, manifest),
        { mono: true },
    );

    if (kind === "cloud") {
        pushSourceRow(
            rows,
            "manifestUrl",
            "sidecarSource.detail.manifestUrl",
            transport?.manifestUrl ?? transport?.sourceUrl,
            { mono: true, url: true },
        );
        pushSourceRow(
            rows,
            "endpoint",
            "sidecarSource.detail.endpoint",
            transport?.endpoint,
            { mono: true, url: true },
        );
        pushSourceRow(
            rows,
            "healthUrl",
            "sidecarSource.detail.healthUrl",
            transport?.healthUrl,
            { mono: true, url: true },
        );
        pushSourceRow(
            rows,
            "sourceUrl",
            "sidecarSource.detail.sourceUrl",
            transport?.sourceUrl && transport.sourceUrl !== transport.manifestUrl
                ? transport.sourceUrl
                : undefined,
            { mono: true, url: true },
        );
        pushSourceRow(
            rows,
            "sourceId",
            "sidecarSource.detail.sourceId",
            transport?.sourceId,
            { mono: true },
        );
    } else {
        pushSourceRow(
            rows,
            "command",
            "sidecarSource.detail.command",
            manifest.command ?? sidecarInfo?.command,
            { mono: true },
        );
    }

    pushSourceRow(
        rows,
        "host",
        "sidecarSource.detail.host",
        getUrlHost(getSidecarPrimaryUrl(sidecarInfo, manifest)),
        { mono: true },
    );

    return dedupeRows(rows);
}

/* ── Main Component ── */

export function SidecarDetailView({ sidecarId }: { sidecarId: string }) {
    const t = useT("editor");
    const i18n = useI18n();
    const setSelectedSidecarId = useEditorStore((s) => s.setSelectedSidecarId);
    const settingsSnapshot = useSettingsStore((s) => s.snapshot);
    const ensureSettingsLoaded = useSettingsStore((s) => s.ensureLoaded);
    const boundPlugins = useMemo(
        () => getPluginsForSidecar(sidecarId),
        [sidecarId],
    );
    const sidecarInfo = useEngineOverviewStore(
        (s) => s.sidecars.find((sidecar) => sidecar.id === sidecarId) ?? null,
    );
    const loading = useEngineOverviewStore((s) => s.loadingScopes.sidecars);
    const retainOverviewScopes = useEngineOverviewStore((s) => s.retainScopes);
    const refreshNow = useEngineOverviewStore((s) => s.refreshNow);

    useEffect(() => {
        return retainOverviewScopes(["sidecars"]);
    }, [retainOverviewScopes]);

    useEffect(() => {
        void ensureSettingsLoaded();
    }, [ensureSettingsLoaded]);

    const handleShutdown = async () => {
        await getPlatformService().engine.sidecarShutdown(sidecarId);
        await refreshNow(["sidecars"]);
    };

    const handleStart = async () => {
        const cmd = sidecarInfo?.manifest?.command ?? sidecarInfo?.command;
        if (!cmd) return;
        try {
            await getPlatformService().engine.sidecarSpawn(sidecarId, cmd);
            await refreshNow(["sidecars"]);
        } catch {
            // ignore — sidecar may need plugin context
        }
    };

    const displayManifest: SidecarManifest = useMemo(() => {
        const manifest = sidecarInfo?.manifest
            ? resolveSidecarManifest(
                sidecarInfo.manifest as unknown as Record<string, unknown>,
                undefined,
                { locale: i18n.locale, fallbackLocale: i18n.fallbackLocale },
            ) as unknown as SidecarManifest
            : null;

        return manifest ?? {
            id: sidecarId,
            name: sidecarId,
            version: "",
            description: sidecarInfo?.command ?? sidecarId,
            command: sidecarInfo?.command ?? sidecarId,
            capabilities: sidecarInfo
                ? sidecarInfo.capabilities.map((c) => ({
                    method: c,
                    description: "",
                }))
                : [],
        };
    }, [
        i18n.fallbackLocale,
        i18n.locale,
        sidecarId,
        sidecarInfo?.capabilities,
        sidecarInfo?.command,
        sidecarInfo?.manifest,
    ]);
    const configFields = useMemo(
        () =>
            deriveSidecarConfigFields(
                sidecarId,
                displayManifest.configuration,
                settingsSnapshot?.view.values,
                t,
            ),
        [displayManifest.configuration, settingsSnapshot?.view.values, sidecarId, t],
    );
    const isCloud = isCloudSidecar(sidecarInfo, displayManifest);

    return (
        <div className="flex flex-col h-full bg-background">
            {/* Top bar */}
            <div className="flex items-center gap-2 h-10 px-4 border-b border-border shrink-0 bg-muted/20">
                <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setSelectedSidecarId(null)}
                >
                    <ArrowLeft size={14} />
                    <span>{t("common.back")}</span>
                </button>
                <div className="w-px h-4 bg-border" />
                <span className="text-xs text-muted-foreground">{t("sidecarDetail.detailTitle")}</span>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-auto">
                <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
                    {/* Header */}
                    <HeaderSection
                        manifest={displayManifest}
                        sidecarInfo={sidecarInfo}
                        isCloud={isCloud}
                        loading={loading}
                        onRefresh={() => {
                            void refreshNow(["sidecars"]);
                        }}
                        onShutdown={handleShutdown}
                        onStart={handleStart}
                        onOpenSettings={
                            displayManifest.configuration?.type === "object"
                                ? () => {
                                    setSelectedSidecarId(null);
                                    useSettingsStore.getState().setActiveSection("sidecar");
                                    useSettingsStore.getState().setSearchQuery(sidecarId);
                                    useEditorStore.getState().setSidebarView("settings");
                                }
                                : undefined
                        }
                    />

                    <Section
                        title={t("sidecarDetail.sourceInfo")}
                        icon={isCloud ? <Cloud size={14} /> : <Server size={14} />}
                        boxed
                    >
                        <SidecarSourceDetails
                            sidecarInfo={sidecarInfo}
                            manifest={displayManifest}
                        />
                    </Section>

                    {/* Overview */}
                    {displayManifest.overview ? (
                        <Section
                            title={t("sidecarDetail.overview")}
                            icon={<Layers size={14} />}
                            boxed
                        >
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {displayManifest.overview}
                            </p>
                        </Section>
                    ) : null}

                    {/* Runtime Status */}
                    <Section title={t("sidecarDetail.runtimeStatus")} icon={<Cpu size={14} />}>
                        <RuntimeStatus
                            sidecarInfo={sidecarInfo}
                            manifest={displayManifest}
                            isCloud={isCloud}
                        />
                    </Section>

                    {configFields.length > 0 ? (
                        <Section
                            title={t("sidecarDetail.configuration")}
                            icon={<Settings2 size={14} />}
                            count={configFields.length}
                        >
                            <SidecarConfigFieldList fields={configFields} />
                        </Section>
                    ) : null}

                    {/* Bound Plugins */}
                    {boundPlugins.length > 0 ? (
                        <Section
                            title={t("sidecarDetail.boundPlugins")}
                            icon={<Server size={14} />}
                            count={boundPlugins.length}
                        >
                            <PluginList plugins={boundPlugins} />
                        </Section>
                    ) : null}

                    {/* Capabilities */}
                    {displayManifest.capabilities.length > 0 ? (
                        <Section
                            title={t("common.capabilities")}
                            icon={<Zap size={14} />}
                        >
                            <CapabilityWorkspace
                                capabilities={displayManifest.capabilities}
                                sidecarId={sidecarId}
                                isRunning={sidecarInfo?.alive ?? false}
                            />
                        </Section>
                    ) : null}

                    {/* Protocol & Build */}
                    <Section title={t("sidecarDetail.protocol")} icon={<Terminal size={14} />}>
                        <div className="rounded-lg border border-border/50 bg-muted/20 p-4 font-mono text-xs text-muted-foreground leading-relaxed">
                            <pre>{isCloud
                                ? `Editor / Engine
  ↕ HTTPS JSON-RPC
${displayManifest.title ?? displayManifest.name}

POST /magic-md/sidecar/rpc
{"jsonrpc":"2.0","id":1,"method":"${displayManifest.capabilities[0]?.method ?? "..."}","params":{...}}`
                                : `Editor (Tauri)
  ↕ stdin / stdout
${displayManifest.name}

Content-Length: N\\r\\n\\r\\n
{"jsonrpc":"2.0","id":1,"method":"${displayManifest.capabilities[0]?.method ?? "..."}","params":{...}}`}</pre>
                        </div>
                    </Section>
                </div>
            </div>
        </div>
    );
}

/* ── Sub-components ── */

function Section({
    title,
    icon,
    count,
    boxed = false,
    children,
}: {
    title: string;
    icon?: React.ReactNode;
    count?: number;
    boxed?: boolean;
    children: React.ReactNode;
}) {
    return (
        <div>
            <SectionHeader icon={icon} title={title} badge={count} />
            {boxed ? (
                <div className="rounded-lg border border-border bg-card p-4">
                    {children}
                </div>
            ) : (
                children
            )}
        </div>
    );
}

function SectionHeader({
    icon,
    title,
    badge,
}: {
    icon?: React.ReactNode;
    title: string;
    badge?: number;
}) {
    return (
        <div className="flex items-center gap-2 mb-3">
            {icon ? <span className="text-muted-foreground">{icon}</span> : null}
            <h2 className="text-sm font-semibold text-foreground">{title}</h2>
            {badge != null ? (
                <span className="text-[10px] font-medium text-muted-foreground bg-muted/40 rounded px-1.5 py-0.5 tabular-nums">
                    {badge}
                </span>
            ) : null}
        </div>
    );
}

function HeaderSection({
    manifest,
    sidecarInfo,
    isCloud,
    loading,
    onRefresh,
    onShutdown,
    onStart,
    onOpenSettings,
}: {
    manifest: SidecarManifest;
    sidecarInfo: SidecarInfo | null;
    isCloud: boolean;
    loading: boolean;
    onRefresh: () => void;
    onShutdown: () => void;
    onStart: () => void;
    onOpenSettings?: () => void;
}) {
    const t = useT("editor");
    const isRunning = sidecarInfo?.alive ?? false;
    const statusLabel = isCloud
        ? isRunning
            ? t("sidecarDetail.cloudConfigured")
            : t("sidecarDetail.cloudNotConfigured")
        : isRunning
            ? t("status.running")
            : t("status.stopped");

    return (
        <div className="flex items-start gap-4">
            {/* Icon */}
            <div
                className={cn(
                    "w-14 h-14 rounded-xl border flex items-center justify-center text-2xl shrink-0",
                    isRunning
                        ? "theme-accent-subtle"
                        : "border-border bg-muted/30",
                )}
                style={isRunning ? getThemeAccentStyle("success") : undefined}
            >
                {manifest.icon ?? "⚙"}
            </div>

            {/* Title & meta */}
            <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        <h1 className="truncate text-xl font-bold text-foreground tracking-tight">
                            {manifest.title ?? manifest.id}
                        </h1>
                        <span
                            className={cn(
                                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                                isRunning
                                    ? "theme-accent-chip"
                                    : "bg-muted/50 text-muted-foreground border border-border",
                            )}
                            style={isRunning ? getThemeAccentStyle("success") : undefined}
                        >
                            {isCloud ? (
                                <Cloud size={9} />
                            ) : isRunning ? (
                                <Activity size={9} />
                            ) : (
                                <Circle size={8} />
                            )}
                            {statusLabel}
                        </span>
                    </div>
                    <SidecarSourceBadge
                        sidecarInfo={sidecarInfo}
                        manifest={manifest}
                        compact
                        className="mt-1 ml-auto"
                    />
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                    {manifest.description}
                </p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {manifest.version ? <Badge>v{manifest.version}</Badge> : null}
                    {isCloud ? <Badge>{t("sidecarDetail.cloudSource")}</Badge> : null}
                    <Badge mono>{isCloud ? (manifest.transport?.type ?? "cloud") : manifest.command}</Badge>
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 shrink-0">
                {onOpenSettings ? (
                    <button
                        type="button"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-muted-foreground border border-border bg-muted/30 hover:bg-muted/60 hover:text-foreground transition-colors"
                        onClick={onOpenSettings}
                    >
                        <Settings2 size={12} />
                        {t("sidecarDetail.action.openSettings")}
                    </button>
                ) : null}
                <button
                    type="button"
                    className="flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                    onClick={onRefresh}
                    title={t("common.refresh")}
                >
                    <RefreshCw size={14} className={cn(loading ? "animate-spin" : "")} />
                </button>
                {!isCloud && isRunning ? (
                    <button
                        type="button"
                        className="theme-accent-button flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                        style={getThemeAccentStyle("error")}
                        onClick={onShutdown}
                    >
                        <PowerOff size={12} />
                        {t("sidecarDetail.action.shutdown")}
                    </button>
                ) : !isCloud ? (
                    <button
                        type="button"
                        className="theme-accent-button flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                        style={getThemeAccentStyle("success")}
                        onClick={onStart}
                    >
                        <Activity size={12} />
                        {t("sidecarDetail.action.start")}
                    </button>
                ) : null}
            </div>
        </div>
    );
}

function Badge({
    children,
    mono,
}: {
    children: React.ReactNode;
    mono?: boolean;
}) {
    return (
        <span
            className={cn(
                "inline-block rounded-md border border-border/50 bg-muted/30 px-1.5 py-0.5 text-[10px] text-muted-foreground",
                mono ? "font-mono" : "",
            )}
        >
            {children}
        </span>
    );
}

function SidecarSourceBadge({
    sidecarInfo,
    manifest,
    compact = false,
    className,
}: {
    sidecarInfo: SidecarInfo | null;
    manifest: SidecarManifest;
    compact?: boolean;
    className?: string;
}) {
    const t = useT("editor");
    const kind = sidecarSourceKindFor(sidecarInfo, manifest);
    const label = t(sidecarSourceLabelKey(kind));
    const sourceUrl = getSidecarPrimaryUrl(sidecarInfo, manifest);
    const tooltip = sourceUrl
        ? `${t(sidecarSourceTooltipKey(kind))}: ${sourceUrl}`
        : t(sidecarSourceTooltipKey(kind));

    return (
        <span
            title={tooltip}
            className={cn(
                "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px]",
                kind === "cloud"
                    ? "border-primary/25 bg-primary/5 text-primary"
                    : "border-border/50 bg-muted/30 text-muted-foreground",
                className,
            )}
        >
            {kind === "cloud" ? (
                <Cloud size={compact ? 10 : 11} className="shrink-0" />
            ) : (
                <Server size={compact ? 10 : 11} className="shrink-0" />
            )}
            {compact ? null : <span>{label}</span>}
        </span>
    );
}

function SidecarSourceDetails({
    sidecarInfo,
    manifest,
}: {
    sidecarInfo: SidecarInfo | null;
    manifest: SidecarManifest;
}) {
    const t = useT("editor");
    const rows = getSidecarSourceDetailRows(sidecarInfo, manifest);
    return (
        <div className="space-y-2">
            {rows.map((row) => {
                const value = row.valueKey ? t(row.valueKey) : row.value;
                return (
                    <div
                        key={`${row.key}:${row.value}`}
                        className="grid gap-1 text-sm sm:grid-cols-[132px_minmax(0,1fr)]"
                    >
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {t(row.labelKey)}
                        </div>
                        {row.href ? (
                            <a
                                href={row.href}
                                target="_blank"
                                rel="noreferrer"
                                className="min-w-0 truncate text-xs text-primary hover:underline"
                                title={value}
                            >
                                {value}
                            </a>
                        ) : (
                            <div
                                className={cn(
                                    "min-w-0 truncate text-xs text-foreground/80",
                                    row.mono && "font-mono",
                                )}
                                title={value}
                            >
                                {value}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function RuntimeStatus({
    sidecarInfo,
    manifest,
    isCloud,
}: {
    sidecarInfo: SidecarInfo | null;
    manifest: SidecarManifest;
    isCloud: boolean;
}) {
    const t = useT("editor");
    if (!sidecarInfo?.alive) {
        return (
            <div className="flex items-center gap-3 text-sm text-muted-foreground/60">
                {isCloud ? (
                    <Cloud size={12} className="text-primary/60" />
                ) : (
                    <Circle size={10} className="text-muted-foreground/30" />
                )}
                <span>
                    {isCloud
                        ? t("sidecarDetail.cloudNotConfiguredHint")
                        : t("sidecarDetail.notRunningHint")}
                </span>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
                <StatusCard
                    label={t("sidecarDetail.activeCapabilities")}
                    value={String(sidecarInfo.capabilities.length)}
                />
                <StatusCard
                    label={t("sidecarDetail.protocol")}
                    value={transportLabel(sidecarInfo, manifest)}
                />
            </div>
            {sidecarInfo.capabilities.length > 0 ? (
                <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    <span className="text-[10px] text-muted-foreground/50 mr-1">
                        {t("common.active")}:
                    </span>
                    {sidecarInfo.capabilities.map((c) => (
                        <span
                            key={c}
                            className="text-[10px] font-mono text-muted-foreground/70"
                        >
                            {c}
                        </span>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function StatusCard({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-lg border border-border/50 bg-muted/10 px-3 py-2">
            <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">
                {label}
            </p>
            <p className="text-sm font-semibold text-foreground mt-0.5 font-mono">
                {value}
            </p>
        </div>
    );
}

function CapabilityWorkspace({
    capabilities,
    sidecarId,
    isRunning,
}: {
    capabilities: SidecarCapability[];
    sidecarId: string;
    isRunning: boolean;
}) {
    const t = useT("editor");
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [query, setQuery] = useState("");
    const [selectedMethod, setSelectedMethod] = useState<string | null>(
        capabilities[0]?.method ?? null,
    );

    useEffect(() => {
        if (selectedMethod && capabilities.some((cap) => cap.method === selectedMethod)) {
            return;
        }
        setSelectedMethod(capabilities[0]?.method ?? null);
    }, [capabilities, selectedMethod]);

    const filteredCapabilities = useMemo(() => {
        const lowered = query.trim().toLowerCase();
        if (!lowered) return capabilities;
        return capabilities.filter((capability) =>
            [capability.method, capability.description]
                .filter((value): value is string => Boolean(value))
                .some((value) => value.toLowerCase().includes(lowered)),
        );
    }, [capabilities, query]);

    const selectedCapability =
        capabilities.find((capability) => capability.method === selectedMethod) ?? null;

    return (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
            <CollapsibleSidebar
                collapsed={sidebarCollapsed}
                storageKey={`sidecar-detail/${sidecarId}/capability-sidebar`}
                defaultWidth={300}
                minWidth={240}
                maxWidth={420}
                minMainWidth={460}
                collapsedWidth={42}
                className="h-[560px] min-h-[420px] min-w-0 overflow-hidden"
                sidebarClassName="border-r border-border/40 bg-card"
                mainClassName="h-full min-h-0 min-w-0 bg-card"
                collapsedSidebar={
                    <button
                        type="button"
                        className="flex h-full w-full items-start justify-center pt-3 text-muted-foreground hover:bg-muted/30 hover:text-foreground"
                        onClick={() => setSidebarCollapsed(false)}
                        title={t("common.expand")}
                    >
                        <PanelLeftOpen size={14} />
                    </button>
                }
                sidebar={
                    <aside className="flex h-full min-h-0 flex-col">
                        <div className="border-b border-border/40 p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <Zap size={12} className="text-muted-foreground shrink-0" />
                                <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground/80">
                                    {t("common.capabilities")}
                                </span>
                                <button
                                    type="button"
                                    className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                    onClick={() => setSidebarCollapsed(true)}
                                    title={t("common.collapse")}
                                >
                                    <PanelLeftClose size={12} />
                                </button>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Filter size={11} className="text-muted-foreground/50 shrink-0" />
                                <input
                                    type="text"
                                    placeholder={t("sidecarDetail.capabilities.filterPlaceholder")}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/40 outline-none"
                                />
                            </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-auto divide-y divide-border/20">
                            {filteredCapabilities.length === 0 ? (
                                <div className="px-3 py-6 text-center">
                                    <p className="text-xs text-muted-foreground/50">
                                        {t("sidecarDetail.capabilities.empty")}
                                    </p>
                                </div>
                            ) : (
                                filteredCapabilities.map((capability) => (
                                    <CapabilityIndexRow
                                        key={capability.method}
                                        capability={capability}
                                        selected={capability.method === selectedMethod}
                                        onSelect={() => setSelectedMethod(capability.method)}
                                    />
                                ))
                            )}
                        </div>
                    </aside>
                }
            >
                {selectedCapability ? (
                    <CapabilityDetailPanel
                        capability={selectedCapability}
                        sidecarId={sidecarId}
                        isRunning={isRunning}
                    />
                ) : (
                    <div className="flex h-full min-h-0 items-center justify-center p-6 text-center">
                        <p className="text-xs text-muted-foreground/50">
                            {t("sidecarDetail.capabilities.noSelection")}
                        </p>
                    </div>
                )}
            </CollapsibleSidebar>
        </div>
    );
}

function CapabilityIndexRow({
    capability,
    selected,
    onSelect,
}: {
    capability: SidecarCapability;
    selected: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            className={cn(
                "relative flex w-full items-center px-3 py-2 text-left transition-colors",
                selected
                    ? "font-semibold text-primary"
                    : "text-muted-foreground hover:text-foreground",
            )}
            onClick={onSelect}
        >
            <span
                className={cn(
                    "absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full transition-opacity",
                    selected ? "bg-primary opacity-100" : "opacity-0",
                )}
            />
            <span className="min-w-0 truncate text-xs font-mono font-medium">
                {capability.method}
            </span>
        </button>
    );
}

function CapabilityDetailPanel({
    capability,
    sidecarId,
    isRunning,
}: {
    capability: SidecarCapability;
    sidecarId: string;
    isRunning: boolean;
}) {
    const t = useT("editor");
    const [activeTab, setActiveTab] = useState<"info" | "debug">("info");
    const paramsTextSeed = useMemo(
        () => buildCapabilityParamsText(capability),
        [capability],
    );
    const [paramsText, setParamsText] = useState(paramsTextSeed);
    const [calling, setCalling] = useState(false);
    const [result, setResult] = useState<{
        success: boolean;
        data?: unknown;
        error?: string;
        durationMs: number;
    } | null>(null);
    const paramEntries = capabilityParamEntries(capability);
    const returnsSummary = capabilityReturnsSummary(capability);

    useEffect(() => {
        setActiveTab("info");
        setParamsText(paramsTextSeed);
        setResult(null);
    }, [capability.method, paramsTextSeed]);

    const handleCall = async () => {
        let parsed: Record<string, unknown>;
        try {
            const raw = JSON.parse(paramsText);
            parsed =
                raw && typeof raw === "object" && !Array.isArray(raw)
                    ? (raw as Record<string, unknown>)
                    : {};
        } catch {
            setResult({ success: false, error: t("sidecarDetail.invalidJson"), durationMs: 0 });
            return;
        }
        setCalling(true);
        setResult(null);
        try {
            const res = await getPlatformService().engine.sidecarCall(
                sidecarId,
                capability.method,
                parsed,
            );
            setResult({
                success: res.success,
                data: res.result,
                error: res.error,
                durationMs: res.durationMs,
            });
        } catch (err) {
            setResult({
                success: false,
                error: err instanceof Error ? err.message : String(err),
                durationMs: 0,
            });
        } finally {
            setCalling(false);
        }
    };

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-card">
            <div className="border-b border-border/40 p-3 space-y-3">
                <div className="flex items-start gap-2 min-w-0">
                    <Zap size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-mono font-semibold text-foreground">
                            {capability.method}
                        </span>
                    </div>
                </div>

                <div
                    role="tablist"
                    aria-label={t("sidecarDetail.capabilities.tabs")}
                    className="flex items-center gap-4 text-xs"
                >
                    {(["info", "debug"] as const).map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === tab}
                            className={cn(
                                "group inline-flex items-center gap-1.5 px-0 py-1 font-medium transition-colors",
                                activeTab === tab
                                    ? "font-semibold text-primary"
                                    : "text-muted-foreground/60 hover:text-foreground",
                            )}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab === "info"
                                ? t("sidecarDetail.capabilities.info")
                                : t("sidecarDetail.debug")}
                        </button>
                    ))}
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto p-3">
                {activeTab === "info" ? (
                    <div className="space-y-4">
                        {capability.description ? (
                            <div>
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                                    {t("common.description")}
                                </span>
                                <p className="mt-1 text-xs leading-relaxed text-muted-foreground/70">
                                    {capability.description}
                                </p>
                            </div>
                        ) : null}

                        <div>
                            <div className="mb-1.5 flex items-center gap-2">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                                    {t("common.parameters")}
                                </span>
                                <span className="text-[10px] font-mono text-muted-foreground/35">
                                    {paramEntries.length}
                                </span>
                            </div>
                            {paramEntries.length === 0 ? (
                                <div className="px-0 py-1 text-[10px] text-muted-foreground/50">
                                    {t("sidecarDetail.capabilities.noParams")}
                                </div>
                            ) : (
                                <div className="rounded-md border border-border/30 overflow-hidden">
                                    <table className="w-full text-[10px]">
                                        <thead>
                                            <tr className="border-b border-border/20 text-muted-foreground/50">
                                                <th className="px-2 py-1 text-left font-semibold uppercase tracking-wider">
                                                    {t("common.name")}
                                                </th>
                                                <th className="px-2 py-1 text-left font-semibold uppercase tracking-wider">
                                                    {t("common.type")}
                                                </th>
                                                <th className="px-2 py-1 text-left font-semibold uppercase tracking-wider">
                                                    {t("common.description")}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/10">
                                            {paramEntries.map((param) => (
                                                <tr key={param.name}>
                                                    <td className="px-2 py-1 font-mono font-medium text-foreground/70">
                                                        {param.name}
                                                        {param.required ? null : (
                                                            <span className="text-muted-foreground/35">?</span>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-1 font-mono text-primary/60">
                                                        {param.type}
                                                    </td>
                                                    <td className="px-2 py-1 text-muted-foreground/60">
                                                        {param.description}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {capability.returns ? (
                            <div>
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                                    {t("sidecarDetail.capabilities.returns")}
                                </span>
                                <div className="mt-1 text-[10px] font-mono text-muted-foreground/70">
                                    {returnsSummary ?? JSON.stringify(capability.returns)}
                                </div>
                            </div>
                        ) : null}

                        {capability.example ? (
                            <div>
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                                    {t("sidecarDetail.capabilities.examples")}
                                </span>
                                <div className="mt-1 space-y-2">
                                    {capability.example.request ? (
                                        <JsonPanel
                                            title={t("common.request")}
                                            value={capability.example.request}
                                        />
                                    ) : null}
                                    {capability.example.response ? (
                                        <JsonPanel
                                            title={t("common.response")}
                                            value={capability.example.response}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {!isRunning ? (
                            <p className="text-[10px] text-muted-foreground/50">
                                {t("sidecarDetail.action.startToDebug")}
                            </p>
                        ) : null}

                        <div className="space-y-1.5">
                            <div className="flex items-center">
                                <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-semibold">
                                    {t("sidecarDetail.params")}
                                </span>
                                <span className="text-[9px] text-muted-foreground/30 ml-auto uppercase">
                                    JSON
                                </span>
                            </div>
                            <textarea
                                value={paramsText}
                                onChange={(e) => setParamsText(e.target.value)}
                                spellCheck={false}
                                className="w-full min-h-[120px] max-h-[260px] rounded-md border border-border/30 bg-transparent px-3 py-2 text-[11px] font-mono leading-relaxed text-foreground/70 resize-y focus:border-primary/40 focus:outline-none"
                                placeholder="{}"
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className={cn(
                                    "theme-accent-button flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                                    (calling || !isRunning) && "cursor-not-allowed opacity-60",
                                )}
                                style={getThemeAccentStyle("warning")}
                                onClick={handleCall}
                                disabled={calling || !isRunning}
                            >
                                {calling ? (
                                    <Loader2 size={12} className="animate-spin" />
                                ) : (
                                    <Play size={12} />
                                )}
                                {calling ? t("sidecarDetail.calling") : t("sidecarDetail.send")}
                            </button>
                            {result ? (
                                <span
                                    className="theme-accent-text text-[10px] font-mono"
                                    style={getThemeAccentStyle(
                                        result.success ? "success" : "error",
                                    )}
                                >
                                    {result.durationMs}ms
                                </span>
                            ) : null}
                        </div>

                        {result ? (
                            <div className="space-y-1">
                                {result.success ? (
                                    <JsonPanel
                                        title={t("common.response")}
                                        value={result.data}
                                    />
                                ) : (
                                    <div
                                        className="theme-accent-banner rounded-md px-3 py-2"
                                        style={getThemeAccentStyle("error")}
                                    >
                                        <p className="mb-0.5 text-[10px] uppercase tracking-wider font-semibold">
                                            {t("common.error")}
                                        </p>
                                        <pre className="whitespace-pre-wrap break-all text-[11px] font-mono">
                                            {result.error}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
}

interface CapabilityParamRow {
    name: string;
    type: string;
    description: string;
    required: boolean;
}

function capabilityParamEntries(capability: SidecarCapability): CapabilityParamRow[] {
    if (!capability.params?.properties) return [];
    return Object.entries(capability.params.properties).map(([name, property]) => {
        const schema = property as Record<string, unknown>;
        return {
            name,
            type: formatSchemaType(schema),
            description: typeof schema.description === "string" ? schema.description : "",
            required: capability.params?.required?.includes(name) ?? false,
        };
    });
}

function buildCapabilityParamsText(capability: SidecarCapability): string {
    if (capability.example?.request?.params) {
        return JSON.stringify(capability.example.request.params, null, 2);
    }
    const params = capabilityParamEntries(capability);
    if (params.length === 0) return "{}";

    const scaffold: Record<string, unknown> = {};
    for (const param of params) {
        const schema = capability.params?.properties?.[param.name] as
            | Record<string, unknown>
            | undefined;
        scaffold[param.name] = schema?.default ?? `<${param.type}>`;
    }
    return JSON.stringify(scaffold, null, 2);
}

function capabilityReturnsSummary(capability: SidecarCapability): string | null {
    const returns = capability.returns as Record<string, unknown> | undefined;
    if (!returns) return null;
    if (returns.properties && typeof returns.properties === "object") {
        return `{ ${Object.keys(returns.properties).join(", ")} }`;
    }
    if (returns.type) return String(returns.type);
    return null;
}

function formatSchemaType(schema: Record<string, unknown>): string {
    if (Array.isArray(schema.enum) && schema.enum.length > 0) {
        return schema.enum.map((value) => JSON.stringify(value)).join(" | ");
    }
    if (Array.isArray(schema.type)) {
        return schema.type.join(" | ");
    }
    return String(schema.type ?? "any");
}

function JsonPanel({ title, value }: { title: string; value: unknown }) {
    return (
        <div className="rounded-md border border-border/30 overflow-hidden">
            <div className="flex items-center px-2.5 py-1 border-b border-border/30">
                <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-semibold">
                    {title}
                </span>
                <span className="text-[9px] text-muted-foreground/30 ml-auto uppercase">
                    JSON
                </span>
            </div>
            <div className="max-h-64 overflow-auto px-3 py-2">
                <JsonTree value={value} defaultExpandDepth={2} />
            </div>
        </div>
    );
}

function PluginList({ plugins }: { plugins: string[] }) {
    return (
        <div className="space-y-1">
            {plugins.map((name) => {
                const plugin = pluginUIRegistry.get(name);
                return (
                    <div
                        key={name}
                        className="flex items-center gap-2 rounded-lg border border-border/30 px-3 py-2"
                    >
                        <div className="w-1 h-6 rounded-full bg-primary/30 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-foreground truncate">
                                {name}
                            </p>
                            {plugin?.manifest.description ? (
                                <p className="text-[10px] text-muted-foreground line-clamp-1">
                                    {plugin.manifest.description}
                                </p>
                            ) : null}
                        </div>
                        {plugin?.manifest.actions && plugin.manifest.actions.length > 0 ? (
                            <div className="flex items-center gap-1 shrink-0">
                                {plugin.manifest.actions.map((a) => (
                                    <span
                                        key={a.name}
                                        className="text-[9px] text-muted-foreground/50 bg-muted/30 rounded px-1 py-0.5"
                                    >
                                        {a.label || a.name}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

interface SidecarConfigField {
    name: string;
    type: string;
    description: string;
    defaultValue?: string;
    currentValue?: string;
}

function SidecarConfigFieldList({
    fields,
}: {
    fields: SidecarConfigField[];
}) {
    const t = useT("editor");
    return (
        <div className="rounded-lg border border-border/40 overflow-hidden">
            <table className="w-full text-left text-xs">
                <thead>
                    <tr className="bg-muted/30 border-b border-border/30">
                        <th className="px-3 py-2 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                            {t("common.name")}
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                            {t("common.type")}
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                            {t("common.description")}
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                            {t("common.current")}
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                            {t("common.default")}
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border/20">
                    {fields.map((field) => (
                        <tr key={field.name} className="hover:bg-muted/20 transition-colors">
                            <td className="px-3 py-2 whitespace-nowrap">
                                <span className="text-xs font-mono font-semibold text-foreground/80">
                                    {field.name}
                                </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                <span className="text-[10px] font-mono text-primary/60">
                                    {field.type}
                                </span>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">
                                {field.description}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                <span className="text-[10px] font-mono text-foreground/70">
                                    {field.currentValue ?? "—"}
                                </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                <span className="text-[10px] font-mono text-muted-foreground/50">
                                    {field.defaultValue ?? "—"}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function deriveSidecarConfigFields(
    sidecarId: string,
    configuration: SidecarManifest["configuration"] | undefined,
    values?: Record<string, unknown>,
    t?: ReturnType<typeof useT>,
): SidecarConfigField[] {
    const fields: SidecarConfigField[] = [];
    const enabledValue = values?.[`sidecars.${sidecarId}.enabled`];

    fields.push({
        name: "enabled",
        type: "boolean",
        description:
            sidecarId === "fs"
                ? (t?.("sidecarDetail.fsEnabledDescription") ?? "Built-in file-system Sidecar. Always enabled.")
                : (t?.("sidecarDetail.enabledDescription") ?? "Allow this Sidecar to auto-start and participate in runtime calls."),
        defaultValue: "true",
        currentValue:
            sidecarId === "fs"
                ? "true"
                : formatDisplayValue(enabledValue === undefined ? true : enabledValue),
    });

    if (!configuration?.properties) {
        return fields;
    }

    const currentSettings = values?.[`sidecars.${sidecarId}.settings`];
    const currentObject =
        currentSettings &&
        typeof currentSettings === "object" &&
        !Array.isArray(currentSettings)
            ? (currentSettings as Record<string, unknown>)
            : {};

    for (const [name, property] of Object.entries(configuration.properties)) {
        const schema = property as Record<string, unknown>;
        const enumValues = Array.isArray(schema.enum) && schema.enum.length > 0
            ? `enum(${schema.enum.map((value) => JSON.stringify(value)).join(" | ")})`
            : null;
        fields.push({
            name,
            type: enumValues ?? String(schema.type ?? "unknown"),
            description: typeof schema.description === "string" ? schema.description : "",
            defaultValue:
                schema.default === undefined
                    ? undefined
                    : formatDisplayValue(schema.default),
            currentValue:
                currentObject[name] === undefined
                    ? undefined
                    : formatDisplayValue(currentObject[name]),
        });
    }

    return fields;
}

function formatDisplayValue(value: unknown): string {
    if (value === null) return "null";
    if (value === undefined) return "—";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}
